/**
 * Configuration for memory leak detection system.
 * This file controls the behavior of memory leak detection across the application.
 */

// Declare process for browser environment
declare const process: {
  env: {
    NODE_ENV: string;
  };
};

export interface MemoryLeakConfig {
  /** Enable memory leak detection globally */
  enabled: boolean;

  /** Enable render tracking for components */
  enableRenderTracking: boolean;

  /** Enable leak detection for async operations */
  enableLeakDetection: boolean;

  /** Warn about excessive renders */
  warnOnExcessiveRenders: boolean;

  /** Warn about components that stay mounted too long */
  warnOnLongMountTime: boolean;

  /** Threshold for number of renders before warning */
  renderThreshold: number;

  /** Threshold for renders per second before warning */
  frequencyThreshold: number;

  /** Threshold for mount time in milliseconds before warning (default: 30 seconds) */
  mountTimeThreshold: number;

  /** Components to exclude from monitoring (by component name) */
  excludedComponents: string[];

  /** Only monitor components that match these patterns */
  includedPatterns: string[];

  /** Enable detailed logging */
  enableDetailedLogging: boolean;

  /** Log level for memory leak warnings */
  logLevel: 'error' | 'warn' | 'info' | 'debug';
}

/**
 * Default configuration for memory leak detection.
 * Only active in development mode.
 */
const defaultConfig: MemoryLeakConfig = {
  enabled: process.env.NODE_ENV === 'development',
  enableRenderTracking: true,
  enableLeakDetection: true,
  warnOnExcessiveRenders: true,
  warnOnLongMountTime: true,
  renderThreshold: 30, // Reduced from 50 to catch issues earlier
  frequencyThreshold: 5, // Reduced from 10 to catch issues earlier
  mountTimeThreshold: 60000, // 60 seconds - warn if component stays mounted longer than this
  excludedComponents: [
    // Add component names to exclude from monitoring
    'DevTools',
    'ReactQueryDevtools',
    'Router',
    // Components that are expected to stay mounted long-term
    'App',
    'Layout',
    'Home',
    'PageLoader',
    'PreferencesSelector',
    'CompanyInfo',
    // Main page sections that stay mounted while on home page (normal SPA behavior)
    'HeroSection',
    'HeroCTAButtons',
    'FeaturedClinics',
    'FeaturedTreatments',
    'AllProviders',
    'FeaturedEquipment',
    'TrustBadges',
    'SectionIndicator',
    // Quality of Life components that enhance UX and stay mounted during home page usage
    'KeyboardShortcutsHint',
    'FloatingCTA',
    // Visual background elements that stay mounted during home page usage
    'Globe',
    // Core UI components that render frequently in lists/grids during user interactions
    'OptimizedImage',
    // Clinic detail page components that mount/unmount with navigation (normal behavior)
    'ClinicHeaderRefactored',
    // Modal components that mount/unmount frequently (expected behavior)
    'ProviderAIChatbotRefactored',
    'SearchBar',
    'Footer',
    'MobileExpandableMenu',
    // Form components that re-render frequently during user interaction
    'InquiryForm',
    'InquiryForm2',
    // Modal components that stay open during user interaction
    'InquiryModal',
    // Image gallery components that users interact with
    'ClinicImageSlider',
    'FullscreenModal',
    // Tooltip components used in content that stays visible
    'MedicalTermTooltip',
    // Treatment detail components that users read
    'TreatmentHero',
    'TreatmentDetail',
    // Treatment list components that users interact with frequently
    'TreatmentsList',
  ],
  includedPatterns: [
    // Only monitor components matching these patterns (empty means monitor all)
    // Examples: 'Filter', 'Modal', 'Search', 'Form'
  ],
  enableDetailedLogging: false,
  logLevel: 'warn',
};

/**
 * Current configuration. Can be overridden at runtime.
 */
let currentConfig: MemoryLeakConfig = { ...defaultConfig };

/**
 * Get the current memory leak detection configuration
 */
export function getMemoryLeakConfig(): MemoryLeakConfig {
  return { ...currentConfig };
}

/**
 * Update memory leak detection configuration
 */
export function setMemoryLeakConfig(config: Partial<MemoryLeakConfig>): void {
  currentConfig = { ...currentConfig, ...config };

  if (process.env.NODE_ENV === 'development') {
    console.log('[MemoryLeakConfig] Configuration updated:', currentConfig);
  }
}

/**
 * Check if a component should be monitored based on current configuration
 */
export function shouldMonitorComponent(componentName: string): boolean {
  if (!currentConfig.enabled) {
    return false;
  }

  // Check exclusions
  if (currentConfig.excludedComponents.includes(componentName)) {
    return false;
  }

  // Check inclusions (if patterns are specified)
  if (currentConfig.includedPatterns.length > 0) {
    return currentConfig.includedPatterns.some(pattern => componentName.includes(pattern));
  }

  return true;
}

/**
 * Reset configuration to defaults
 */
export function resetMemoryLeakConfig(): void {
  currentConfig = { ...defaultConfig };

  if (process.env.NODE_ENV === 'development') {
    console.log('[MemoryLeakConfig] Configuration reset to defaults');
  }
}

/**
 * Enable/disable memory leak detection globally
 */
export function setMemoryLeakDetectionEnabled(enabled: boolean): void {
  currentConfig.enabled = enabled;

  if (process.env.NODE_ENV === 'development') {
    console.log(`[MemoryLeakConfig] Memory leak detection ${enabled ? 'enabled' : 'disabled'}`);
  }
}

/**
 * Development helper to configure memory leak detection from browser console
 * Usage in browser console:
 * window.configureMemoryLeakDetection({ renderThreshold: 20, frequencyThreshold: 5 })
 */
declare global {
  interface Window {
    configureMemoryLeakDetection?: (config: Partial<MemoryLeakConfig>) => void;
    getMemoryLeakConfig?: () => MemoryLeakConfig;
    resetMemoryLeakConfig?: () => void;
  }
}

// Add global helpers in development
if (process.env.NODE_ENV === 'development' && typeof window !== 'undefined') {
  window.configureMemoryLeakDetection = setMemoryLeakConfig;
  window.getMemoryLeakConfig = getMemoryLeakConfig;
  window.resetMemoryLeakConfig = resetMemoryLeakConfig;
}
